export type Sistema = "frances" | "aleman" | "americano";

export const SISTEMAS: { id: Sistema; nombre: string; desc: string }[] = [
  { id: "frances", nombre: "Sistema Francés", desc: "Cuota fija" },
  { id: "aleman", nombre: "Sistema Alemán", desc: "Capital fijo" },
  { id: "americano", nombre: "Sistema Americano", desc: "Bullet" },
];

export interface Periodo {
  periodo: number;
  fecha?: string;
  saldoInicial: number;
  interes: number;
  capital: number;
  cuota: number;
  saldoFinal: number;
}

export interface Tabla {
  periodos: Periodo[];
  totalIntereses: number;
  totalCapital: number;
  totalPagado: number;
  costoPct: number;
  monto: number;
}

const r2 = (n: number) => Math.round(n * 100) / 100;

export function fmtMXN(n: number): string {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);
}

export function fmtPct(n: number, d = 2): string {
  return `${new Intl.NumberFormat("es-MX", { minimumFractionDigits: d, maximumFractionDigits: d }).format(Number.isFinite(n) ? n : 0)}%`;
}

export function cuotaFrancesa(M: number, i: number, n: number): number {
  if (i === 0) return M / n;
  return (M * i) / (1 - Math.pow(1 + i, -n));
}

function fechasPeriodo(inicio: string | undefined, n: number): (string | undefined)[] {
  if (!inicio) return Array(n).fill(undefined);
  const [y, m, d] = inicio.split("-").map(Number);
  if (!y || !m || !d) return Array(n).fill(undefined);
  const out: string[] = [];
  for (let k = 0; k < n; k++) {
    const base = new Date(Date.UTC(y, m - 1 + k, 1));
    const ultimoDia = new Date(
      Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + 1, 0),
    ).getUTCDate();
    const fecha = new Date(
      Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), Math.min(d, ultimoDia)),
    );
    out.push(
      new Intl.DateTimeFormat("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      }).format(fecha),
    );
  }
  return out;
}

export interface Params {
  monto: number;
  tasaMensual: number; // en %
  cuotas: number;
  primerPago?: string;
  seguroPct?: number; // % sobre saldo insoluto (para CAT)
}

export function generarTabla(sistema: Sistema, p: Params): Tabla {
  const M = p.monto;
  const i = p.tasaMensual / 100;
  const n = Math.max(1, Math.floor(p.cuotas));
  const fechas = fechasPeriodo(p.primerPago, n);
  const periodos: Periodo[] = [];
  let saldo = M;

  const cuotaF = cuotaFrancesa(M, i, n);
  const capitalFijo = M / n;

  for (let t = 1; t <= n; t++) {
    const saldoInicial = saldo;
    let interes = saldoInicial * i;
    let capital: number;
    if (sistema === "frances") capital = cuotaF - interes;
    else if (sistema === "aleman") capital = capitalFijo;
    else capital = t === n ? saldoInicial : 0;

    if (t === n) capital = saldoInicial; // ajuste de redondeo: saldo final exacto 0
    interes = r2(interes);
    capital = r2(capital);
    const saldoFinal = r2(saldoInicial - capital);
    periodos.push({
      periodo: t,
      ...(fechas[t - 1] ? { fecha: fechas[t - 1] as string } : {}),
      saldoInicial: r2(saldoInicial),
      interes,
      capital,
      cuota: r2(interes + capital),
      saldoFinal: Math.abs(saldoFinal) < 0.005 ? 0 : saldoFinal,
    });
    saldo = saldoInicial - capital;
  }

  const totalIntereses = r2(periodos.reduce((a, x) => a + x.interes, 0));
  const totalCapital = r2(periodos.reduce((a, x) => a + x.capital, 0));
  const totalPagado = r2(totalIntereses + totalCapital);
  return {
    periodos,
    totalIntereses,
    totalCapital,
    totalPagado,
    costoPct: M > 0 ? (totalIntereses / M) * 100 : 0,
    monto: M,
  };
}

export interface CatParams extends Params {
  sistema: Sistema;
  aperturaActiva: boolean;
  aperturaPct: number;
  aperturaIva: boolean;
  adminActiva: boolean;
  adminMonto: number;
  adminIva: boolean;
  seguroActivo: boolean;
  seguroPct: number;
}

export interface CatResultado {
  tabla: Tabla;
  comisionApertura: number;
  comisionAperturaConIva: number;
  montoNeto: number;
  adminConIva: number;
  cuotaBase: number;
  pagoTotalMensual: number;
  flujos: number[];
  tasaPeriodica: number;
  cat: number;
}

const IVA = 0.16;

export function calcularCat(p: CatParams): CatResultado {
  const tabla = generarTabla(p.sistema, p);
  const apertura = p.aperturaActiva ? (p.monto * p.aperturaPct) / 100 : 0;
  const aperturaConIva = r2(apertura * (p.aperturaIva ? 1 + IVA : 1));
  const montoNeto = r2(p.monto - aperturaConIva);
  const admin = p.adminActiva ? p.adminMonto : 0;
  const adminConIva = r2(admin * (p.adminIva ? 1 + IVA : 1));

  const flujos = tabla.periodos.map((per) => {
    const seguro = p.seguroActivo ? (per.saldoInicial * p.seguroPct) / 100 : 0;
    return r2(per.cuota + adminConIva + seguro);
  });

  const vpn = (i: number) =>
    flujos.reduce((a, f, idx) => a + f / Math.pow(1 + i, idx + 1), 0) - montoNeto;

  let lo = 0;
  let hi = 5;
  let mid = 0;
  if (vpn(lo) <= 0) {
    mid = 0;
  } else {
    for (let k = 0; k < 400; k++) {
      mid = (lo + hi) / 2;
      const v = vpn(mid);
      if (Math.abs(v) < 1e-9) break;
      if (v > 0) lo = mid;
      else hi = mid;
    }
  }
  const cat = (Math.pow(1 + mid, 12) - 1) * 100;
  return {
    tabla,
    comisionApertura: r2(apertura),
    comisionAperturaConIva: aperturaConIva,
    montoNeto,
    adminConIva,
    cuotaBase: tabla.periodos[0]?.cuota ?? 0,
    pagoTotalMensual: flujos[0] ?? 0,
    flujos,
    tasaPeriodica: mid,
    cat,
  };
}

export function exportarCSV(nombre: string, tabla: Tabla, conFecha: boolean) {
  const head = [
    "Periodo",
    ...(conFecha ? ["Fecha"] : []),
    "Saldo inicial",
    "Interes",
    "Capital",
    "Cuota total",
    "Saldo final",
  ];
  const rows = tabla.periodos.map((p) =>
    [
      p.periodo,
      ...(conFecha ? [p.fecha ?? ""] : []),
      p.saldoInicial,
      p.interes,
      p.capital,
      p.cuota,
      p.saldoFinal,
    ].join(","),
  );
  const total = [
    "TOTAL",
    ...(conFecha ? [""] : []),
    "",
    tabla.totalIntereses,
    tabla.totalCapital,
    tabla.totalPagado,
    0,
  ].join(",");
  const csv = [head.join(","), ...rows, total].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${nombre}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
